/**
 * moduleActionFocus — 1.422.0 tiling P2.5 (double-fire guard).
 *
 * The 'vibe:module-action' bus is a window CustomEvent every mounted module
 * listens to. Pre-tiling only ONE module was mounted, so generic verbs
 * ('refresh', 'open_external') were unambiguous. With concurrent tiles, an
 * untargeted verb would fire in EVERY open module. Rule:
 *
 *   1. detail.module_id present → only that module handles it (the P3
 *      voice/Pi verb threading sends this). Matches the FULL id exactly, OR a
 *      SHORT name equal to the module's last dotted segment (live-found
 *      2026-07-03: LLM agents emit `"mattermost"` for `gamemagick.mattermost`;
 *      an exact-only match dropped the event silently). Case-insensitive.
 *   2. no target + multiple tiles open → only the FOCUSED tile handles it.
 *   3. no target + single module (incl. the derive-only fallback tile,
 *      where openModuleIds is empty) → handle it (pre-tiling behavior).
 */
import { useUIStore } from '@/stores/uiStore';

export function shouldHandleModuleAction(
  myModuleId: string,
  detail: { module_id?: unknown } | undefined,
): boolean {
  const target = typeof detail?.module_id === 'string' && detail.module_id ? detail.module_id : null;
  if (target) {
    if (target === myModuleId) return true;
    // Short-name resolution: match the last dotted segment exactly (case-
    // insensitive). Exact-segment (not substring) keeps it deterministic and
    // non-leaky across modules — the [On screen now] block lists both the full
    // id and this short name. NB: no registry is available here (each module
    // knows only its own id), so this is a local suffix check, not resolveModuleId.
    const lastSegment = myModuleId.split('.').pop() ?? myModuleId;
    return target.toLowerCase() === lastSegment.toLowerCase();
  }
  const { openModuleIds, activeModuleId } = useUIStore.getState();
  if (openModuleIds.length > 1) return activeModuleId === myModuleId;
  return true;
}

/**
 * P3 — resolve an LLM-supplied module reference to a real module id. Agents
 * mostly send exact ids (the [On screen now] block lists them) but voice
 * transcription can hand back a display name ("the peptide project"), so fall
 * back to a case-insensitive display-name match (exact, then substring —
 * substring only when it's unambiguous).
 */
export function resolveModuleId(
  raw: unknown,
  available: { moduleId: string; displayName: string }[],
): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const needle = raw.trim();
  const exact = available.find((a) => a.moduleId === needle);
  if (exact) return exact.moduleId;
  const lower = needle.toLowerCase();
  const byName = available.find((a) => a.displayName.toLowerCase() === lower);
  if (byName) return byName.moduleId;
  const bySub = available.filter(
    (a) =>
      a.displayName.toLowerCase().includes(lower) || a.moduleId.toLowerCase().includes(lower),
  );
  return bySub.length === 1 ? bySub[0].moduleId : null;
}
